import { Link } from "react-router-dom";
import { useWeeklyTimesheets } from "@/hooks/admin/useAdminData";

export default function WeeklyTimesheets() {
  const {
    rows,
    weekStartLabel,
    weekEndLabel,
    totalHours,
    totalOT,
    totalPay,
    loading,
  } = useWeeklyTimesheets();

  const rangeLabel =
    weekStartLabel && weekEndLabel
      ? `${weekStartLabel} – ${weekEndLabel}, 2026 · Payroll preview`
      : "Payroll preview";

  return (
    <div className="bg-white border border-ink-100 rounded-lg">
      <div className="px-5 pt-5 pb-4 border-b border-ink-100 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="font-display text-base font-bold text-ink-900">Weekly Timesheets</h3>
          <p className="text-[11px] text-ink-500 mt-0.5">{rangeLabel}</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md border border-ink-200 text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap">
            <i className="ri-arrow-left-s-line"></i>
            Prev Week
          </button>
          <button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-ink-900 hover:bg-ink-800 text-white cursor-pointer whitespace-nowrap">
            <i className="ri-file-download-line"></i>
            Export Payroll
          </button>
        </div>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-3 gap-px bg-ink-100 border-b border-ink-100">
        <div className="bg-white px-5 py-3">
          <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
            Total Hours
          </div>
          <div className="font-display text-xl font-bold text-ink-900 tabular-nums">
            {loading ? "—" : `${totalHours.toFixed(1)}h`}
          </div>
        </div>
        <div className="bg-white px-5 py-3">
          <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
            Overtime
          </div>
          <div className="font-display text-xl font-bold text-amber-700 tabular-nums">
            {loading ? "—" : `${totalOT.toFixed(1)}h`}
          </div>
        </div>
        <div className="bg-white px-5 py-3">
          <div className="text-[10px] uppercase tracking-wider text-ink-400 font-semibold">
            Gross Payroll
          </div>
          <div className="font-display text-xl font-bold text-ink-900 tabular-nums">
            {loading ? "—" : `$${totalPay.toLocaleString()}`}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto scrollbar-thin">
        {loading ? (
          <div className="py-10 text-center text-sm text-ink-500">
            <i className="ri-loader-4-line animate-spin mr-2"></i>
            Building timesheets…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-10 text-center">
            <div className="w-12 h-12 rounded-full bg-cream-100 text-ink-500 mx-auto mb-2 flex items-center justify-center">
              <i className="ri-time-line text-xl"></i>
            </div>
            <div className="text-sm font-semibold text-ink-900">No completed shifts this week</div>
            <div className="text-xs text-ink-500">
              Hours appear here once crew clock out.
            </div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-cream-50 border-b border-ink-100 text-ink-500">
                <th className="text-left font-semibold uppercase text-[10px] tracking-wider px-5 py-2.5">
                  Crew
                </th>
                <th className="text-left font-semibold uppercase text-[10px] tracking-wider px-5 py-2.5">
                  Shifts
                </th>
                <th className="text-left font-semibold uppercase text-[10px] tracking-wider px-5 py-2.5">
                  Regular
                </th>
                <th className="text-left font-semibold uppercase text-[10px] tracking-wider px-5 py-2.5">
                  OT
                </th>
                <th className="text-left font-semibold uppercase text-[10px] tracking-wider px-5 py-2.5">
                  Utilization
                </th>
                <th className="text-right font-semibold uppercase text-[10px] tracking-wider px-5 py-2.5">
                  Gross Pay
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => {
                const utilPct = Math.min(100, Math.round((t.hours / 40) * 100));
                return (
                  <tr
                    key={t.crewId}
                    className="border-b border-ink-100 last:border-b-0 hover:bg-cream-50 transition-colors"
                  >
                    <td className="px-5 py-3">
                      <Link
                        to={`/crew/${t.crewId}`}
                        className="flex items-center gap-3 group cursor-pointer"
                      >
                        <div
                          className={`w-8 h-8 rounded-full ${t.color} text-white text-[10px] font-bold flex items-center justify-center shrink-0`}
                        >
                          {t.initials}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-ink-900 group-hover:text-brand-700 truncate">
                            {t.name}
                          </div>
                          <div className="text-[11px] text-ink-500">{t.role}</div>
                        </div>
                      </Link>
                    </td>
                    <td className="px-5 py-3 text-sm font-semibold text-ink-700 tabular-nums">
                      {t.shifts}
                    </td>
                    <td className="px-5 py-3 text-sm font-semibold text-ink-900 tabular-nums">
                      {t.regularHours.toFixed(1)}h
                    </td>
                    <td className="px-5 py-3 text-sm font-semibold tabular-nums">
                      {t.otHours > 0 ? (
                        <span className="text-amber-700">{t.otHours.toFixed(1)}h</span>
                      ) : (
                        <span className="text-ink-300">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2 min-w-[120px]">
                        <div className="flex-1 h-1.5 rounded-full bg-cream-100 overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              utilPct >= 100 ? "bg-amber-500" : "bg-brand-500"
                            }`}
                            style={{ width: `${utilPct}%` }}
                          ></div>
                        </div>
                        <span className="text-[11px] font-bold text-ink-700 tabular-nums">
                          {utilPct}%
                        </span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right text-sm font-bold text-ink-900 tabular-nums">
                      ${t.pay.toLocaleString()}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}